import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Cls, Emojis } from 'app/constants';
import { toSeasonWeekID } from 'helpers/IdHelpers';
import { loadSyncStatusFromDB } from 'lib/idb/IndexDbInterface';
import {
  AppThunk,
  DBsInitState,
  FirebaseCollections,
  Maybe,
  RootState,
  SeasonCollectionStatus,
  SeasonID,
  SeasonWeekID,
  SeasonWeekStatus,
  Stores,
  SyncIDBPayload,
  SyncIDBStores,
  SyncManagerProps,
  SyncManagerStatus,
  SyncServerObservation,
  SyncStoreProps,
  WeekID,
} from 'types';

const initialState: SyncManagerProps = {
  serverObservation: { state: 'idle' },
  id: 'SyncManagerStatus',
  initializing: false,
  dbsInitState: {
    firebaseDBInit: false,
    indexDBInit: false,
  },
  seasons: [],
  beLastUpdatedOn: 0,
  serverLastUpdatedOn: '',
  notes: '',
  clientLastUpdatedOn: '',
  feLastUpdatedOn: 0,
  idbStatus: {
    gameSchedule: { id: Stores.GameSchedule, lastUpdatedOn: 0, timestamp: '' } as SyncStoreProps,
    teamInfo: { id: Stores.TeamInfo, lastUpdatedOn: 0, timestamp: '' } as SyncStoreProps,
    gameStats: { id: Stores.GameStats, lastUpdatedOn: 0, timestamp: '' } as SyncStoreProps,
    teamWeeklyStats: { id: Stores.TeamWeeklyStats, lastUpdatedOn: 0, timestamp: '' } as SyncStoreProps,
    seasonStandings: { id: Stores.SeasonStandings, lastUpdatedOn: 0, timestamp: '' } as SyncStoreProps,
    weeklyRanks: { id: Stores.WeeklyRanks, lastUpdatedOn: 0, timestamp: '' } as SyncStoreProps,
    predictions: { id: Stores.Predictions, lastUpdatedOn: 0, timestamp: '' } as SyncStoreProps,
  } as SyncIDBStores,
};

export type StoreSyncName =
  | 'schedule'
  | 'gameStats'
  | 'teamGameStats'
  | 'teamInfo'
  | 'standings'
  | 'ranks'
  | 'teamsOnBye'
  | 'predictions'
  | 'headToHead';

export type SyncMapper = {
  fe: Stores;
  be: FirebaseCollections;
};

export type StoreMapperType = {
  [key in StoreSyncName]: SyncMapper;
};

const StoreMapper: StoreMapperType = {
  schedule: { fe: Stores.GameSchedule, be: FirebaseCollections.Schedule },
  gameStats: { fe: Stores.GameStats, be: FirebaseCollections.MatchResults },
  teamGameStats: { fe: Stores.TeamWeeklyStats, be: FirebaseCollections.TeamMatchResults },
  teamInfo: { fe: Stores.TeamInfo, be: FirebaseCollections.Teams },
  standings: { fe: Stores.SeasonStandings, be: FirebaseCollections.Standings },
  ranks: { fe: Stores.WeeklyRanks, be: FirebaseCollections.Rankings },
  predictions: { fe: Stores.Predictions, be: FirebaseCollections.Predictions },
  teamsOnBye: { fe: Stores.TeamsOnByeWeek, be: FirebaseCollections.Schedule },
  headToHead: { fe: Stores.HeadToHead, be: FirebaseCollections.HeadToHead },
  // teamsOnBye is not a separate collection in Firebase, it's part of stats/${seasonID} document
  // syncStatus is also not a collection, it's a document containing all sync status information for the Firebase
};

export const initializeSyncManager = (): AppThunk => async (dispatch, getState) => {
  const state = getState();

  // ✅ Prevent multiple initializations
  if (state.sync.initializing || state.sync.dbsInitState.indexDBInit) {
    return;
  }

  dispatch(setSyncManagerInitializing(true));

  try {
    // Load from IndexedDB
    await loadSyncStatusFromDB({
      storeName: Stores.SyncStatus,
      callbackFn: (data: SyncIDBPayload) => {
        dispatch(setSyncManagerWithIDBData(data));
        dispatch(setIndexDBInit(true));
      },
      shouldFetch: true,
      year: '2024',
    });
  } catch (error) {
    // dispatch(setSyncManagerError(error));
    console.log(`${Emojis.warning} Local Sync Status %c ERROR %c ${error}`, Cls.Cri, '');
  } finally {
    dispatch(setSyncManagerInitializing(false));
  }
};

export const syncManagerSlice = createSlice({
  name: 'sync',
  initialState,
  reducers: {
    setSyncServerObservation: (state, action: PayloadAction<SyncServerObservation>) => {
      state.serverObservation = action.payload;
      if (['idle', 'connecting', 'missing'].includes(action.payload.state)) {
        state.dbsInitState.firebaseDBInit = false;
      }
    },
    setSyncManagerInitializing: (state, action: PayloadAction<boolean>) => {
      state.initializing = action.payload;
    },
    setSyncManagerWithFBData: (state, action: PayloadAction<Partial<SyncManagerStatus>>) => {
      // A cached replay must not overwrite a newer status already observed in this app.
      state.dbsInitState.firebaseDBInit = true;
      if ((action.payload.lastUpdatedOn ?? 0) < state.beLastUpdatedOn) return;
      const tempSeasons = [...(action.payload.seasons || [])];
      tempSeasons.sort((a, b) => a.seasonID.localeCompare(b.seasonID));
      state.seasons = tempSeasons;
      state.beLastUpdatedOn = action.payload.lastUpdatedOn || 0;
      state.serverLastUpdatedOn = action.payload.timestamp || '';
      state.notes = action.payload.notes || '';
    },
    setSyncManagerWithIDBData: (state, action: PayloadAction<SyncIDBPayload>) => {
      state.idbStatus = { ...action.payload.syncStores };
      state.feLastUpdatedOn = action.payload.feLastUpdatedOn;
      state.clientLastUpdatedOn = action.payload.clientLastUpdatedOn;
    },
    setClientLastUpdatedOn: (state, action: PayloadAction<string>) => {
      state.clientLastUpdatedOn = action.payload;
    },
    setFELastUpdatedOn: (state, action: PayloadAction<number>) => {
      state.feLastUpdatedOn = action.payload;
    },
    setBELastUpdatedOn: (state, action: PayloadAction<number>) => {
      state.beLastUpdatedOn = action.payload;
    },
    setDBsInitState: (state, action: PayloadAction<DBsInitState>) => {
      state.dbsInitState = action.payload;
    },
    setFirebaseDBInit: (state, action: PayloadAction<boolean>) => {
      state.dbsInitState.firebaseDBInit = action.payload;
    },
    setIndexDBInit: (state, action: PayloadAction<boolean>) => {
      state.dbsInitState.indexDBInit = action.payload;
    },
  },
});

export const {
  setSyncServerObservation,
  setSyncManagerInitializing,
  setSyncManagerWithFBData,
  setClientLastUpdatedOn,
  setSyncManagerWithIDBData,
  setFELastUpdatedOn,
  setBELastUpdatedOn,
  setDBsInitState,
  setFirebaseDBInit,
  setIndexDBInit,
} = syncManagerSlice.actions;

export const getSyncManager = (state: RootState): SyncManagerStatus => ({
  id: state.sync.id,
  seasons: state.sync.seasons,
  notes: state.sync.notes,
  lastUpdatedOn: state.sync.beLastUpdatedOn,
  timestamp: state.sync.serverLastUpdatedOn,
  lastUpdatedBy: 'feSystem',
});
export const getClientLastUpdatedOn = (state: RootState): string => state.sync.clientLastUpdatedOn;

export const getBESyncSeasons = (state: RootState): SeasonCollectionStatus[] => state.sync.seasons;
export const getIDBStatus = (state: RootState): SyncIDBStores => state.sync.idbStatus;

const shouldFetchStore = (state: RootState, mapper: SyncMapper): boolean => {
  const beSeasons = getBESyncSeasons(state);
  const idbStatus = getIDBStatus(state);

  if (beSeasons.length === 0) return false;
  const latestSeason = beSeasons.length - 1;
  const beLastUpdatedOn = beSeasons[latestSeason].lastUpdatedOn;
  const latestWeek = beSeasons[latestSeason].weeks.findLast((week) => week.completed);
  const latestWeekLastUpdatedOn = latestWeek?.[mapper.be].lastUpdatedOn || 0;
  const idbLastUpdatedOn = idbStatus[mapper.fe]?.lastUpdatedOn || 0;

  return beLastUpdatedOn > idbLastUpdatedOn || latestWeekLastUpdatedOn > idbLastUpdatedOn;
};

export const getShouldFetchGameSchedule = (state: RootState): boolean => {
  return shouldFetchStore(state, StoreMapper.schedule);
};
export const getShouldFetchGameStats = (state: RootState): boolean => {
  return shouldFetchStore(state, StoreMapper.gameStats);
};
export const getShouldFetchTeamGameStats = (state: RootState): boolean => {
  return shouldFetchStore(state, StoreMapper.teamGameStats);
};
export const getShouldFetchTeamInfo = (state: RootState): boolean => {
  return shouldFetchStore(state, StoreMapper.teamInfo);
};
export const getShouldFetchStandings = (state: RootState): boolean => {
  return shouldFetchStore(state, StoreMapper.standings);
};
export const getShouldFetchRanks = (state: RootState): boolean => {
  return shouldFetchStore(state, StoreMapper.ranks);
};
export const getShouldFetchPredictions = (state: RootState): boolean => {
  return shouldFetchStore(state, StoreMapper.predictions);
};
// TODO: Fix this, since teamsOnBye is not a separate collection
export const getShouldFetchTeamsOnBye = (state: RootState): boolean => {
  return shouldFetchStore(state, StoreMapper.teamsOnBye);
};
export const getShouldFetchHeadToHead = (state: RootState): boolean => {
  return shouldFetchStore(state, StoreMapper.headToHead);
};

export const getSyncServerObservation = (state: RootState): SyncServerObservation => state.sync.serverObservation;

export const getDBsInitState = (state: RootState): DBsInitState => state.sync.dbsInitState;
export const getFirebaseDBInit = (state: RootState): boolean => state.sync.dbsInitState.firebaseDBInit;
export const getIndexDBInit = (state: RootState): boolean => state.sync.dbsInitState.indexDBInit;
export const getIsSyncInitialized = (state: RootState): boolean =>
  state.sync.dbsInitState.firebaseDBInit && state.sync.dbsInitState.indexDBInit;

/**
 * Selector to get the collection status for a specific season.
 *
 * @param state - The root state
 * @param seasonID - The season ID to get the collection status for.
 *
 * @return {Maybe<SeasonCollectionStatus>} The collection status for the specified season, or undefined if not found.
 */
export const getSeasonCollectionStatus = (state: RootState, seasonID: SeasonID): Maybe<SeasonCollectionStatus> => {
  const syncManager = getSyncManager(state);
  return syncManager.seasons.find((season: SeasonCollectionStatus) => season.seasonID === seasonID);
};

/**
 * Selector to get the week status for a specific season and week.
 *
 * @param {RootState} state - The root state
 * @param {SeasonID} seasonID - The season ID to get the week status for.
 * @param {WeekID} weekID - The week ID to get the status for.
 *
 * @return {Maybe<SeasonWeekStatus>} The week status for the specified season and week, or undefined if not found.
 */
export const getSeasonWeekStatus = (state: RootState, seasonID: SeasonID, weekID: WeekID): Maybe<SeasonWeekStatus> => {
  const seasonStatus = getSeasonCollectionStatus(state, seasonID);
  return seasonStatus?.weeks.find((s) => s.weekID === weekID);
};

/**
 * Selector to get the last updated timestamp for a specific season and week.
 *
 * @param {RootState} state - The root state
 * @param {SeasonID} seasonID - The season ID to get the last updated timestamp for.
 * @param {WeekID} weekID - The week ID to get the last updated timestamp for.
 *
 * @return {Maybe<number>} The last updated timestamp for the specified season and week, or undefined if not found.
 */
export const getLastUpdatedOnInSeasonWeekStatus = (
  state: RootState,
  seasonID: SeasonID,
  weekID: WeekID
): Maybe<number> => {
  const weekStatus = getSeasonWeekStatus(state, seasonID, weekID);
  return weekStatus?.lastUpdatedOn;
};

/**
 * Memoized selector to get the last completed week for a specific season.
 *
 * @param {RootState} state - The root state
 * @param {SeasonID} seasonID - The season ID to get the last completed week for
 *
 * @return {Maybe<SeasonWeekID>} The last completed week ID, or undefined if not found
 */
export const getLastCompletedWeekBySeasonID = createSelector(
  [
    (state: RootState) => state.sync.seasons,
    (_state: RootState, seasonID: SeasonID) => seasonID,
  ],
  (seasons, seasonID): Maybe<SeasonWeekID> => {
    const seasonWeeks = seasons.find((season) => season.seasonID === seasonID)?.weeks;
    if (!seasonWeeks) return undefined;
    const lastCompletedWeek = seasonWeeks.slice().reverse().find((week) => week.completed);
    return lastCompletedWeek ? toSeasonWeekID(seasonID, lastCompletedWeek.weekID) : undefined;
  }
);

export default syncManagerSlice.reducer;
